import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faVolumeUp, faSearch, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import axios from '../../Api';

const AlertsList = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unresolved');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [surveyFilter, setSurveyFilter] = useState('');
  const [surveys, setSurveys] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const selectedAlertDismiss = useModalDismiss(() => setSelectedAlert(null), { backdropClose: false, enabled: selectedAlert });
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveMessage, setResolveMessage] = useState('');
  const [resolveError, setResolveError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const alertsPerPage = 10;

  useEffect(() => {
    fetchSurveys();
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [currentPage, filter, searchTerm, dateFilter, surveyFilter]);

  const fetchSurveys = async () => {
    try {
      const response = await axios.get('/control/surveys', {
        params: {
          per_page: 1000 // Get all surveys for the filter dropdown
        }
      });
      // Handle both paginated and non-paginated responses
      if (response.data.data && Array.isArray(response.data.data)) {
        setSurveys(response.data.data);
      } else if (Array.isArray(response.data)) {
        setSurveys(response.data);
      } else {
        setSurveys([]);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setSurveys([]);
    }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: alertsPerPage
      };
      if (filter === 'resolved') params.resolved = true;
      if (filter === 'unresolved') params.resolved = false;
      if (searchTerm) params.search = searchTerm;
      if (dateFilter) params.date = dateFilter;
      if (surveyFilter) params.survey_id = surveyFilter;
      const response = await axios.get('/control/alerts', { params });
      setAlerts(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
      setTotalAlerts(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      // Error is silently logged, no alert needed
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchQuery);
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleDateFilter = (date) => {
    setDateFilter(date);
    setCurrentPage(1);
  };

  const handleSurveyFilter = (surveyId) => {
    setSurveyFilter(surveyId);
    setCurrentPage(1);
  };

  const handleResolve = async (alertId) => {
    try {
      setResolving(true);
      setResolveMessage('');
      setResolveError('');
      await axios.post(`/control/alerts/${alertId}/resolve`, {
        resolution_note: resolutionNote
      });
      setResolveMessage('Alerte résolue avec succès');
      // Close modal after 1.5 seconds
      setTimeout(() => {
        setSelectedAlert(null);
        setResolutionNote('');
        setResolveMessage('');
        fetchAlerts();
      }, 1500);
    } catch (error) {
      console.error('Error resolving alert:', error);
      setResolveError(error.response?.data?.message || 'Erreur lors de la résolution de l\'alerte');
    } finally {
      setResolving(false);
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      validate: 'Validé',
      reject: 'Rejeté',
      hold: 'En attente'
    };
    return labels[action] || action;
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Alertes de Contrôle</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-2">Conflits détectés entre deux contrôles</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4 sm:p-6">
        <div className="space-y-4">
          {/* Status Filters */}
          <div className="flex gap-2 flex-wrap">
            {['unresolved', 'resolved'].map((status) => (
              <button
                key={status}
                onClick={() => handleFilterChange(status)}
                className={`px-4 py-2 rounded transition-colors text-sm sm:text-base ${
                  filter === status
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {status === 'unresolved' ? 'Non Résolues' : 'Résolues'}
              </button>
            ))}
          </div>
          
          {/* Search, Survey and Date Filters */}
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Rechercher par enquêteur, contrôleur, ID soumission..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 min-w-0 h-10 px-3 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <button
                  onClick={handleSearch}
                  className="h-10 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>
            </div>
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Enquête</label>
              <select
                value={surveyFilter}
                onChange={(e) => handleSurveyFilter(e.target.value)}
                className="w-full sm:w-auto h-10 px-3 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 min-w-[200px]"
              >
                <option value="">Toutes les enquêtes</option>
                {surveys.map((survey) => (
                  <option key={survey.id} value={survey.id}>
                    {survey.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => handleDateFilter(e.target.value)}
                className="w-full sm:w-auto h-10 px-3 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      ) : (
        <>
          {/* Alerts List */}
          <div className="space-y-4">
            {alerts.map((alert) => (
              <div key={alert.id} className="bg-white rounded-lg shadow-lg border border-gray-200">
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                        Alerte #{alert.id} - Soumission #{alert.submission_id}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">
                        Créée le {new Date(alert.created_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                    {!alert.resolved && (
                      <button
                        onClick={() => setSelectedAlert(alert)}
                        className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-shrink-0"
                      >
                        Résoudre
                      </button>
                    )}
                  </div>

                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded-r">
                    <p className="text-sm text-red-700">{alert.reason}</p>
                  </div>

              {/* Submission Info */}
              {alert.submission && (
                <div className="bg-gray-50 border rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Informations sur la Soumission</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                    <div>
                      <span className="font-medium">ID Soumission:</span> {alert.submission.response_id}
                    </div>
                    {alert.first_check?.assignment?.interviewer && (
                      <div>
                        <span className="font-medium">Enquêteur:</span> {alert.first_check.assignment.interviewer.name}
                        <span className="text-gray-500"> ({alert.first_check.assignment.interviewer.ref})</span>
                      </div>
                    )}
                    {alert.submission.post && (
                      <div>
                        <span className="font-medium">Poste:</span> {alert.submission.post.number}
                        {alert.submission.post.label && (
                          <span className="text-gray-500"> - {alert.submission.post.label}</span>
                        )}
                      </div>
                    )}
                    {alert.first_check?.tel_value && (
                      <div>
                        <span className="font-medium">Tel de client:</span> {alert.first_check.tel_value}
                      </div>
                    )}
                    {alert.submission.survey && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Enquête:</span> 
                        <span className="text-gray-600">{alert.submission.survey.title}</span>
                        <span className="text-gray-500">(ID: {alert.submission.survey.limesurvey_id})</span>
                      </div>
                    )}
                  </div>
                  {alert.submission.survey && alert.submission.response_id && (
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <a
                        href={`https://defineresearch.online/index.php/admin/dataentry/sa/editdata/subaction/edit/surveyId/${alert.submission.survey.limesurvey_id}/id/${alert.submission.response_id}/browseLang`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faFileAlt} />
                        Voir Questionnaire
                      </a>
                      <a
                        href="https://192.168.1.253/index.php?menu=monitoring"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faVolumeUp} />
                        Audio
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Check */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">Premier Contrôle</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Date:</span>{' '}
                      {alert.first_check?.checked_at ? new Date(alert.first_check.checked_at).toLocaleString('fr-FR') : 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Contrôleur:</span> {alert.first_check?.controller?.name}
                    </div>
                    <div>
                      <span className="font-medium">Action:</span>{' '}
                      <span className={`px-2 py-1 rounded text-xs ${
                        alert.first_check?.action === 'validate' ? 'bg-green-100 text-green-800' :
                        alert.first_check?.action === 'reject' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {getActionLabel(alert.first_check?.action)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Note:</span> {alert.first_check?.rating}/5
                    </div>
                    <div>
                      <span className="font-medium">Correction:</span>{' '}
                      {alert.first_check?.correction_applied ? 'Oui' : 'Non'}
                    </div>
                    {alert.first_check?.comment && (
                      <div>
                        <span className="font-medium">Commentaire:</span>
                        <p className="mt-1 text-gray-600">{alert.first_check.comment}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Second Check */}
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <h4 className="font-semibold text-gray-900 mb-3">Deuxième Contrôle</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Date:</span>{' '}
                      {alert.second_check?.checked_at ? new Date(alert.second_check.checked_at).toLocaleString('fr-FR') : 'N/A'}
                    </div>
                    <div>
                      <span className="font-medium">Contrôleur:</span> {alert.second_check?.controller?.name}
                    </div>
                    <div>
                      <span className="font-medium">Action:</span>{' '}
                      <span className={`px-2 py-1 rounded text-xs ${
                        alert.second_check?.action === 'validate' ? 'bg-green-100 text-green-800' :
                        alert.second_check?.action === 'reject' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {getActionLabel(alert.second_check?.action)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Note:</span> {alert.second_check?.rating}/5
                    </div>
                    <div>
                      <span className="font-medium">Correction:</span>{' '}
                      {alert.second_check?.correction_applied ? 'Oui' : 'Non'}
                    </div>
                    {alert.second_check?.comment && (
                      <div>
                        <span className="font-medium">Commentaire:</span>
                        <p className="mt-1 text-gray-600">{alert.second_check.comment}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {alert.resolved && (
                <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4">
                  <p className="text-sm text-green-700">
                    <span className="font-medium">Résolu par:</span> {alert.resolver?.name || 'N/A'} le{' '}
                    {new Date(alert.resolved_at).toLocaleString('fr-FR')}
                  </p>
                  {alert.resolution_note && (
                    <p className="text-sm text-green-700 mt-2">
                      <span className="font-medium">Note:</span> {alert.resolution_note}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {alerts.length === 0 && (
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-lg">Aucune alerte {filter === 'resolved' ? 'résolue' : 'à traiter'}</p>
          </div>
        )}
      </div>
        
      {/* Pagination */}
      {!loading && alerts.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
              Affichage de {((currentPage - 1) * alertsPerPage) + 1} à {Math.min(currentPage * alertsPerPage, totalAlerts)} sur {totalAlerts} alertes
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              {[...Array(totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 sm:px-4 py-2 text-sm rounded-md transition-colors ${
                        currentPage === pageNum
                          ? 'bg-red-600 text-white'
                          : 'bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                  return <span key={pageNum} className="px-2">...</span>;
                }
                return null;
              })}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Resolution Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 border border-gray-200 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Résoudre l'Alerte #{selectedAlert.id}</h2>
            
            {/* Success Message */}
            {resolveMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">{resolveMessage}</p>
              </div>
            )}

            {/* Error Message */}
            {resolveError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{resolveError}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note de résolution (optionnel)
              </label>
              <textarea
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                rows="4"
                disabled={resolving || !!resolveMessage}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Ajoutez une note expliquant la résolution..."
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleResolve(selectedAlert.id)}
                disabled={resolving || !!resolveMessage}
                className="w-full sm:flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {resolving ? 'Résolution...' : 'Résoudre'}
              </button>
              <button
                onClick={() => {
                  setSelectedAlert(null);
                  setResolutionNote('');
                  setResolveMessage('');
                  setResolveError('');
                }}
                disabled={resolving || !!resolveMessage}
                className="w-full sm:flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed transition-colors"
              >
                {resolveMessage ? 'Fermer' : 'Annuler'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsList;

